import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TripCard = ({ trip, currentLanguage, onEdit, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const translations = {
    en: {
      departure: 'Departure',
      return: 'Return',
      capacity: 'Capacity',
      categories: 'Allowed Categories',
      flightInfo: 'Flight Information',
      airline: 'Airline',
      flightNumber: 'Flight',
      departureAirport: 'From',
      arrivalAirport: 'To',
      departureTime: 'Departs',
      arrivalTime: 'Arrives',
      terminal: 'Terminal',
      flightStatus: 'Status',
      edit: 'Edit',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this trip?'
    },
    ko: {
      departure: '출발',
      return: '귀국',
      capacity: '용량',
      categories: '허용 카테고리',
      flightInfo: '항공편 정보',
      airline: '항공사',
      flightNumber: '항공편',
      departureAirport: '출발',
      arrivalAirport: '도착',
      departureTime: '출발 시간',
      arrivalTime: '도착 시간',
      terminal: '터미널',
      flightStatus: '상태',
      edit: '수정',
      delete: '삭제',
      confirmDelete: '이 여행을 삭제하시겠습니까?'
    }
  };

  const t = translations?.[currentLanguage];

  const countryNames = {
    en: {
      us: 'United States',
      jp: 'Japan',
      kr: 'South Korea',
      cn: 'China',
      uk: 'United Kingdom',
      fr: 'France',
      de: 'Germany',
      au: 'Australia'
    },
    ko: {
      us: '미국',
      jp: '일본',
      kr: '대한민국',
      cn: '중국',
      uk: '영국',
      fr: '프랑스',
      de: '독일',
      au: '호주'
    }
  };

  const categoryNames = {
    en: {
      electronics: 'Electronics',
      fashion: 'Fashion',
      cosmetics: 'Cosmetics',
      food: 'Food',
      books: 'Books',
      sports: 'Sports',
      toys: 'Toys',
      health: 'Health'
    },
    ko: {
      electronics: '전자제품',
      fashion: '패션',
      cosmetics: '화장품',
      food: '식품',
      books: '도서',
      sports: '스포츠',
      toys: '장난감',
      health: '건강'
    }
  };

  const flightStatusNames = {
    en: {
      scheduled: 'Scheduled',
      delayed: 'Delayed',
      cancelled: 'Cancelled',
      completed: 'Completed',
      in_flight: 'In Flight'
    },
    ko: {
      scheduled: '예정됨',
      delayed: '지연',
      cancelled: '취소됨',
      completed: '완료',
      in_flight: '비행 중'
    }
  };

  const getCountryName = (code) => {
    return countryNames?.[currentLanguage]?.[code] || code;
  };

  const getCategoryName = (category) => {
    return categoryNames?.[currentLanguage]?.[category] || category;
  };

  const getFlightStatusName = (status) => {
    return flightStatusNames?.[currentLanguage]?.[status] || status;
  };

  const getFlightStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'text-primary';
      case 'in_flight':
        return 'text-success';
      case 'delayed':
        return 'text-warning';
      case 'cancelled':
        return 'text-destructive';
      case 'completed':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString?.split(':');
    return `${hours}:${minutes}`;
  };

  const handleDelete = async () => {
    if (window.confirm(t?.confirmDelete)) {
      setDeleting(true);
      try {
        await onDelete(trip?.id);
      } catch (error) {
        console.error('Delete trip error:', error);
      } finally {
        setDeleting(false);
      }
    }
  };

  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border hover:border-primary/50 transition-base">
      {/* Route Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Icon name="MapPin" size={18} color="var(--color-primary)" />
            <span>{getCountryName(trip?.fromCountry)}</span>
            <Icon name="ArrowRight" size={16} />
            <span>{getCountryName(trip?.toCountry)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            iconName="Edit"
            onClick={() => onEdit(trip)}
          >
            {t?.edit}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="Trash2"
            onClick={handleDelete}
            loading={deleting}
            className="text-destructive hover:text-destructive"
          >
            {t?.delete}
          </Button>
        </div>
      </div>

      {/* Trip Details */}
      <div className="space-y-3">
        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
            <div>
              <div className="text-xs text-muted-foreground">{t?.departure}</div>
              <div className="font-medium text-foreground">{formatDate(trip?.departureDate)}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
            <div>
              <div className="text-xs text-muted-foreground">{t?.return}</div>
              <div className="font-medium text-foreground">{formatDate(trip?.returnDate)}</div>
            </div>
          </div>
        </div>

        {/* Capacity */}
        <div className="flex items-center gap-2 text-sm">
          <Icon name="Package" size={14} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">{t?.capacity}:</span>
          <span className="font-semibold text-primary">{trip?.capacityKg} kg</span>
        </div>

        {/* Categories */}
        <div>
          <div className="text-xs text-muted-foreground mb-2">{t?.categories}</div>
          <div className="flex flex-wrap gap-2">
            {trip?.allowedCategories?.map((category) => (
              <span
                key={category}
                className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-medium"
              >
                {getCategoryName(category)}
              </span>
            ))}
          </div>
        </div>

        {/* Flight Information */}
        {trip?.flightInfo && (
          <div className="border-t border-border pt-3 mt-3">
            <div className="flex items-center justify-between text-xs">
              <div>
                <div className="text-muted-foreground">{t?.flightInfo}</div>
                <div className="font-semibold text-foreground">
                  {trip?.flightInfo?.airline} {trip?.flightInfo?.flightNumber}
                </div>
              </div>
              <div className="text-right">
                <div className="text-muted-foreground">{t?.departureAirport}</div>
                <div className="font-semibold text-foreground">
                  {trip?.flightInfo?.departureAirport}
                  {trip?.flightInfo?.departureTerminal && ` - ${t?.terminal} ${trip?.flightInfo?.departureTerminal}`}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {/* Departure */}
              <div className="bg-background/50 rounded p-2">
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <div className="text-muted-foreground">{t?.departureTime}</div>
                    <div className="font-semibold text-foreground">{formatTime(trip?.flightInfo?.departureTime)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-muted-foreground">{t?.departureAirport}</div>
                    <div className="font-semibold text-foreground">
                      {trip?.flightInfo?.departureAirport}
                      {trip?.flightInfo?.departureTerminal && ` - ${t?.terminal} ${trip?.flightInfo?.departureTerminal}`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arrival */}
              <div className="bg-background/50 rounded p-2">
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <div className="text-muted-foreground">{t?.arrivalTime}</div>
                    <div className="font-semibold text-foreground">{formatTime(trip?.flightInfo?.arrivalTime)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-muted-foreground">{t?.arrivalAirport}</div>
                    <div className="font-semibold text-foreground">
                      {trip?.flightInfo?.arrivalAirport}
                      {trip?.flightInfo?.arrivalTerminal && ` - ${t?.terminal} ${trip?.flightInfo?.arrivalTerminal}`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Flight Status */}
              {trip?.flightInfo?.flightStatus && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{t?.flightStatus}:</span>
                  <span className={`font-semibold ${getFlightStatusColor(trip?.flightInfo?.flightStatus)}`}>
                    {getFlightStatusName(trip?.flightInfo?.flightStatus)}
                  </span>
                </div>
              )}

              {/* Ticket Verified Badge */}
              {trip?.flightInfo?.ticketVerified && (
                <div className="flex items-center gap-1 text-xs text-success">
                  <Icon name="CheckCircle" size={12} />
                  <span>{currentLanguage === 'en' ? 'Ticket Verified' : '항공권 확인됨'}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripCard;